import { Calendar } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useWorkerPlanQuery } from "@/domains/shifts/hooks/use-shift-queries";
import type { Worker } from "@/gql/graphql";
import { Timeline } from "@/lib/components/timeline";
import {
  createWeekColumns,
  generateBiweeklyWorkerTimeline,
  transformPlanToWorkerTimeline,
} from "./timeline-utils";

interface WorkersTimelineOverviewProps {
  workers: Array<{
    worker: Worker;
    plan?: any;
    workerSalonId: string;
  }>;
  className?: string;
}

// Hook to fetch a single worker's plan - must be used at top level of a component
function useWorkerPlan(worker: Worker, workerSalonId: string) {
  const { plan, loading } = useWorkerPlanQuery({
    workerSalonId,
    skip: !workerSalonId,
  });
  return { worker, workerSalonId, plan, loading };
}

// Component that fetches one worker's plan and reports data up via callback
function WorkerPlanFetcher({
  worker,
  workerSalonId,
  onData,
}: {
  worker: Worker;
  workerSalonId: string;
  onData: (data: { worker: Worker; workerSalonId: string; plan: any; loading: boolean }) => void;
}) {
  const data = useWorkerPlan(worker, workerSalonId);
  // Report data change upwards
  useEffect(() => {
    onData(data);
  });
  return null;
}

// Hook to build timeline from already-fetched worker plan data
function useWorkerTimelines(
  workerPlans: Array<{ worker: Worker; workerSalonId: string; plan: any; loading: boolean }>
) {
  const [weeklyTimelineItems, setWeeklyTimelineItems] = useState<any[]>([]);
  const [evenWeekItems, setEvenWeekItems] = useState<any[]>([]);
  const [oddWeekItems, setOddWeekItems] = useState<any[]>([]);

  // Create a stable serialized version of the plan data for dependency tracking
  const _plansKey = useMemo(() => {
    return workerPlans
      .filter(({ loading }) => !loading)
      .map(({ worker, plan }) => `${worker.id}:${plan?.id ?? "null"}`)
      .join(",");
  }, [workerPlans]);

  useEffect(() => {
    const weeklyItems: any[] = [];
    const evenItems: any[] = [];
    const oddItems: any[] = [];

    // First, analyze all plans to determine what timeline types we need
    const hasAnyBiweeklyTemplates = workerPlans
      .filter(({ plan, loading }) => !loading && plan)
      .some(({ plan }) => {
        const scheduleType =
          plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;
        return scheduleType?.includes("biweekly");
      });

    workerPlans
      .filter(({ plan, loading }) => !loading && plan)
      .forEach(({ worker, plan }) => {
        // Determine the schedule type of this worker
        const scheduleType =
          plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;

        const isBiweekly = scheduleType?.includes("biweekly");
        const workerName =
          `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.trim() || "Worker";

        if (hasAnyBiweeklyTemplates) {
          // If ANY worker has biweekly template, show ALL workers in biweekly view
          if (isBiweekly) {
            // For biweekly workers, show their schedule in the appropriate timeline
            const timelineData = generateBiweeklyWorkerTimeline(plan, worker);
            if (timelineData.items.length > 1) {
              // Add to both even and odd timelines with proper schedule visibility
              evenItems.push(timelineData.items[0]); // Even week item
              oddItems.push(timelineData.items[1]); // Odd week item
            }
          } else {
            // For weekly workers, show them in BOTH even and odd timelines
            // This ensures they appear in the biweekly view with consistent schedule
            const baseTimeline = transformPlanToWorkerTimeline(plan, worker);
            const workerItem = baseTimeline.items[0];

            // Create items for both even and odd weeks (weekly workers work both)
            const evenWeekItem = {
              ...workerItem,
              id: `${worker.id}-even`,
              name: workerName,
              // Weekly workers show their schedule in both timelines
            };

            const oddWeekItem = {
              ...workerItem,
              id: `${worker.id}-odd`,
              name: workerName,
              // Weekly workers show their schedule in both timelines
            };

            evenItems.push(evenWeekItem);
            oddItems.push(oddWeekItem);
          }
        } else {
          // No biweekly templates assigned - show traditional weekly timeline
          const timelineData = transformPlanToWorkerTimeline(plan, worker);
          weeklyItems.push(timelineData.items[0]);
        }
      });

    setWeeklyTimelineItems(weeklyItems);
    setEvenWeekItems(evenItems);
    setOddWeekItems(oddItems);
  }, [workerPlans.filter]); // Use stable string key as dependency

  // Determine if we should show biweekly or weekly view
  const hasAnyBiweeklyTemplates = workerPlans
    .filter(({ plan, loading }) => !loading && plan)
    .some(({ plan }) => {
      const scheduleType =
        plan.planType === "TEMPLATE" ? plan.template?.scheduleType : plan.customScheduleType;
      return scheduleType?.includes("biweekly");
    });

  return {
    weeklyTimelineItems,
    evenWeekItems,
    oddWeekItems,
    hasWeekly: weeklyTimelineItems.length > 0 && !hasAnyBiweeklyTemplates, // Only show weekly if no biweekly templates
    hasBiweekly: hasAnyBiweeklyTemplates, // Show biweekly view if ANY biweekly template exists
    hasAnyPlans:
      weeklyTimelineItems.length > 0 || evenWeekItems.length > 0 || oddWeekItems.length > 0,
    hasAnyBiweeklyTemplates, // Expose this for conditional rendering
  };
}

export function WorkersTimelineOverview({ workers, className = "" }: WorkersTimelineOverviewProps) {
  const columns = createWeekColumns(); // Use regular week columns - worker names will be in left sidebar
  const workersWithSalonIds = workers.filter(({ workerSalonId }) => workerSalonId);
  const [workerPlansMap, setWorkerPlansMap] = useState<
    Map<string, { worker: Worker; workerSalonId: string; plan: any; loading: boolean }>
  >(new Map());

  const handleWorkerData = useCallback(
    (data: { worker: Worker; workerSalonId: string; plan: any; loading: boolean }) => {
      setWorkerPlansMap((prev) => {
        const next = new Map(prev);
        next.set(data.workerSalonId, data);
        return next;
      });
    },
    []
  );

  const workerPlansForTimeline = workersWithSalonIds.map(({ worker, workerSalonId }) => ({
    worker,
    workerSalonId,
    plan: workerPlansMap.get(workerSalonId)?.plan ?? null,
    loading: workerPlansMap.get(workerSalonId)?.loading ?? true,
  }));

  const {
    weeklyTimelineItems,
    evenWeekItems,
    oddWeekItems,
    hasWeekly,
    hasAnyPlans,
    hasAnyBiweeklyTemplates,
  } = useWorkerTimelines(workerPlansForTimeline);

  if (workersWithSalonIds.length === 0 || !hasAnyPlans) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              No worker schedules to display. Assign plans to workers to see their schedules.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate total unique workers (avoiding double-counting in biweekly view)
  const totalWorkers = hasAnyBiweeklyTemplates
    ? Math.max(evenWeekItems.length, oddWeekItems.length) // In biweekly view, workers appear in both timelines
    : weeklyTimelineItems.length;

  const timelineTitle = hasAnyBiweeklyTemplates
    ? "Biweekly Schedule Overview"
    : "Weekly Schedule Overview";

  const timelineDescription = hasAnyBiweeklyTemplates
    ? "Biweekly templates detected - showing all workers in even/odd week view for complete schedule visibility"
    : "View all worker schedules in a unified weekly timeline (Monday to Sunday)";

  return (
    <Card className={className}>
      {/* Hidden fetcher components - each calls useWorkerPlanQuery at its top level */}
      {workersWithSalonIds.map(({ worker, workerSalonId }) => (
        <WorkerPlanFetcher
          key={workerSalonId}
          worker={worker}
          workerSalonId={workerSalonId}
          onData={handleWorkerData}
        />
      ))}
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">{timelineTitle}</CardTitle>
          <Badge variant="outline" className="text-xs">
            {totalWorkers} worker{totalWorkers !== 1 ? "s" : ""}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-2">{timelineDescription}</p>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="space-y-6">
          {/* Weekly Timeline */}
          {hasWeekly && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded bg-blue-500" />
                <h3 className="text-sm font-medium">Weekly Schedules</h3>
                <Badge variant="outline" className="text-xs">
                  {weeklyTimelineItems.length} worker{weeklyTimelineItems.length !== 1 ? "s" : ""}
                </Badge>
              </div>
              <Timeline
                items={weeklyTimelineItems}
                columns={columns}
                workingHoursStart={6}
                workingHoursEnd={20}
                showTimeScale={true}
                showCurrentTime={false}
                showHourMarkers={true}
                className="border-0 shadow-none [&_.p-1.text-xs.text-white]:text-center [&_.p-1.text-xs.text-white]:items-center"
                renderItemLabel={(item) => (
                  <div className="pr-4 min-w-0">
                    <div className="text-sm font-medium truncate">{item.name}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Even Week Timeline */}
          {evenWeekItems.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded bg-green-500" />
                <h3 className="text-sm font-medium">
                  Biweekly Schedules - Even Weeks (2, 4, 6, 8...)
                </h3>
                <Badge variant="outline" className="text-xs">
                  {evenWeekItems.length} worker{evenWeekItems.length !== 1 ? "s" : ""}
                </Badge>
              </div>
              <Timeline
                items={evenWeekItems}
                columns={columns}
                workingHoursStart={6}
                workingHoursEnd={20}
                showTimeScale={true}
                showCurrentTime={false}
                showHourMarkers={true}
                className="border-0 shadow-none [&_.p-1.text-xs.text-white]:text-center [&_.p-1.text-xs.text-white]:items-center"
                renderItemLabel={(item) => (
                  <div className="pr-4 min-w-0">
                    <div className="text-sm font-medium truncate">{item.name}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Odd Week Timeline */}
          {oddWeekItems.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-2 h-2 rounded bg-orange-500" />
                <h3 className="text-sm font-medium">
                  Biweekly Schedules - Odd Weeks (1, 3, 5, 7...)
                </h3>
                <Badge variant="outline" className="text-xs">
                  {oddWeekItems.length} worker{oddWeekItems.length !== 1 ? "s" : ""}
                </Badge>
              </div>
              <Timeline
                items={oddWeekItems}
                columns={columns}
                workingHoursStart={6}
                workingHoursEnd={20}
                showTimeScale={true}
                showCurrentTime={false}
                showHourMarkers={true}
                className="border-0 shadow-none [&_.p-1.text-xs.text-white]:text-center [&_.p-1.text-xs.text-white]:items-center"
                renderItemLabel={(item) => (
                  <div className="pr-4 min-w-0">
                    <div className="text-sm font-medium truncate">{item.name}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Schedule Summary */}
          {hasAnyPlans && (
            <div className="border-t pt-4">
              {hasAnyBiweeklyTemplates ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="font-medium">Workers in Even Weeks</div>
                    <div className="text-muted-foreground">{evenWeekItems.length}</div>
                  </div>
                  <div>
                    <div className="font-medium">Workers in Odd Weeks</div>
                    <div className="text-muted-foreground">{oddWeekItems.length}</div>
                  </div>
                  <div>
                    <div className="font-medium">Total Workers</div>
                    <div className="text-muted-foreground">{totalWorkers}</div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium">Weekly Schedules</div>
                    <div className="text-muted-foreground">{weeklyTimelineItems.length}</div>
                  </div>
                  <div>
                    <div className="font-medium">Total Workers</div>
                    <div className="text-muted-foreground">{totalWorkers}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legend */}
          <div className="border-t pt-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                <strong>Legend:</strong>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <p>
                  • <strong>Colored blocks:</strong> Scheduled work shifts
                </p>
                <p>
                  • <strong>Empty spaces:</strong> No scheduled work
                </p>
                {hasAnyBiweeklyTemplates && (
                  <>
                    <p>
                      • <strong>Even weeks:</strong> Weeks 2, 4, 6, 8, 10, 12...
                    </p>
                    <p>
                      • <strong>Odd weeks:</strong> Weeks 1, 3, 5, 7, 9, 11...
                    </p>
                    <p>
                      • <strong>Weekly workers:</strong> Appear in both timelines
                    </p>
                    <p>
                      • <strong>Biweekly workers:</strong> Only appear in their assigned weeks
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
